'use client';
import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { differenceInDays, startOfDay } from 'date-fns'
import { useRouter, usePathname } from 'next/navigation'
import { FaTrophy, FaArrowLeft, FaEdit, FaClone, FaUsers, FaMailBulk, FaMap } from 'react-icons/fa'
import { 
  Button, Chip,
  Navbar, 
  NavbarItem, 
  NavbarContent, 
  NavbarMenuToggle, 
  NavbarMenu,
  Popover, PopoverContent, PopoverTrigger,
  Listbox, ListboxItem } from '@heroui/react'

import HeaderImage from './header-image'
import { useUser } from '../contexts/user-context-v2'
import { useChallenge } from '../contexts/challenge-context'
import { useGroup } from '../contexts/group-context'
import useChallengeStatus from '../hooks/use-challenge-status'
import { formatMetric, displayMeasurement, formatNumber, formatDuration, formatDate } from '../src/formatter'
const Menu = dynamic(() => import('./menu'), { ssr: false });

const ChallengeSubNav = ({ section, page, challenge }) => {
  const router = useRouter()
  const { user } = useUser()
  const path = usePathname()
  
  // Match the end of the path instead of checking for exact template patterns
  if (path.endsWith('/feed')) {
    page = 'challenge-feed'
  } else if (path.endsWith('/dashboard')) {
    page = 'dashboard'
  } else if (path.endsWith('/activities')) {
    page = 'activities'
  } else if (path.endsWith('/edit')) {
    section = 'challenge-admin'
    page = 'edit'
  } else if (path.endsWith('/invite')) {
    section = 'challenge-admin'
    page = 'invite'
  } else if (path.endsWith('/participants')) {
    section = 'challenge-admin'
    page = 'participants'
  } 

  const [isAdminPopoverOpen, setIsAdminPopoverOpen] = useState(false)
  if (!challenge) return null
  return (
    <div className='w-full md:ml-[300px] md:max-w-[calc(100vw-300px)] bg-chTan-100 -mt-5 pb-2 px-4'>
      <div className='flex gap-2 w-full md:max-w-[450px] px-0'>
        <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'challenges' && page === 'challenge-feed' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`} 
            onClick={() => {
              if (page !== 'challenge-feed') {
                router.push(`/challenge/${challenge.id}/feed`) 
              }
            }}
            >Feed</Chip>
        </div>
        <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'challenges' && page === 'dashboard' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              if (page !== 'dashboard') {
                router.push(`/challenge/${challenge.id}/dashboard`) 
              }
            }}
            >Dashboard</Chip>
        </div>
        <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'challenges' && page === 'activities' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              if (page !== 'activities') {
                router.push(`/challenge/${challenge.id}/activities`) 
              }
            }}
            >Activities</Chip>
        </div>
        {(user && challenge?.group?.organizationId === user?.organizationId) &&
          <div className='w-full'>
            <Popover placement='bottom-start' size='lg' isOpen={isAdminPopoverOpen} onOpenChange={(open) => setIsAdminPopoverOpen(open)}>
              <PopoverTrigger className="w-full">
                <Chip 
                  size='sm' 
                  color='primary' 
                  className={`w-full !min-w-full text-center cursor-pointer ${section === 'challenge-admin' ? '' : 'bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
                  >Admin Tools</Chip>
              </PopoverTrigger>
              <PopoverContent>
                <Listbox 
                  aria-label="Main Menu" 
                  itemClasses={{ 
                    base: 'rounded-none text-chDarkBlue data-[hover=true]:bg-chTan-300', 
                    title: 'text-md' 
                  }}>
                    <ListboxItem 
                      startContent={<FaEdit className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/challenge/${challenge.id}/edit`}
                      title='Edit Challenge' 
                      />
                    <ListboxItem 
                      startContent={<FaUsers className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/challenge/${challenge.id}/participants`}
                      title='Participants' 
                      />
                    <ListboxItem 
                      startContent={<FaMailBulk className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/challenge/${challenge.id}/invite`}
                      title='Invite'
                      />
                    <ListboxItem 
                      startContent={<FaClone className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/challenge/clone-${challenge.id}/edit?cloneId=${challenge.id}`}
                      title='Clone' 
                      />
                    {(user?.isAdmin || (user?.adminRef && user?.adminRef.id !== user?.id)) && <ListboxItem 
                      startContent={<FaMap className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/challenge/${challenge.id}/map-edit`}
                      title='Map' 
                      />}
                </Listbox>
              </PopoverContent>
            </Popover>
          </div>
    }
      </div>
    </div>
  )
}

const GroupSubNav = ({ section, page, group }) => {
  const { user } = useUser()
  const router = useRouter()
  const path = usePathname()
  
  const [isAdminPopoverOpen, setIsAdminPopoverOpen] = useState(false)
  if (!group) return null
  return (
    <div className='w-full md:ml-[300px] md:max-w-[calc(100vw-300px)] bg-chTan-100 -mt-5 pb-2 px-4'>
      <div className='flex gap-2 w-full md:max-w-[450px] px-0'>
        <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'groups' && page === 'challenges' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'} capitalize`} 
            onClick={() => {
              if (page !== 'challenges') {
                router.push(`/group/${group.id}`) 
              }
            }}
            >{group.type}s</Chip>
        </div>
        <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'groups' && page === 'activities' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              if (page !== 'activities') {
                router.push(`/group/${group.id}/activities`) 
              }
            }}
            >Activities</Chip>
        </div>
        <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'groups' && page === 'results' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              if (page !== 'results') {
                router.push(`/group/${group.id}/results`) 
              }
            }}
            >Results</Chip>
        </div>
        {(group?.organizationId === user?.organizationId) && 
          <div className='w-full'>
            <Popover placement='bottom-start' size='lg' isOpen={isAdminPopoverOpen} onOpenChange={(open) => setIsAdminPopoverOpen(open)}>
              <PopoverTrigger className="w-full">
                <Chip 
                  size='sm' 
                  color='primary' 
                  className={`w-full !min-w-full text-center cursor-pointer ${section === 'group-admin' ? '' : 'bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
                  >Admin Tools</Chip>
              </PopoverTrigger>
              <PopoverContent>
                <Listbox 
                  aria-label="Main Menu" 
                  itemClasses={{ 
                    base: 'rounded-none text-chDarkBlue data-[hover=true]:bg-chTan-300', 
                    title: 'text-md' 
                  }}>
                    <ListboxItem 
                      startContent={<FaEdit className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/group/${group.id}/edit`}
                      as={Link}
                      prefetch={false}
                      title='Edit Group' 
                      />
                    <ListboxItem 
                      startContent={<FaUsers className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/challenge/new/edit?groupId=${group.id}`}
                      title={<span className='capitalize'>Add {group.type}</span>}
                      as={Link}
                      prefetch={false}
                      />
                    <ListboxItem 
                      startContent={<FaMap className='text-xl text-chTan-600' />}
                      onPress={() => {
                        setIsAdminPopoverOpen(false)  
                      }}
                      href={`/group/${group.id}/map-edit`}
                      title='Map Edit'
                      as={Link}
                      prefetch={false}
                      />
                </Listbox>
              </PopoverContent>
            </Popover>
          </div>
        }
      </div>
    </div>
  )
}

const getNavbarStats = ({ user, challengeStatus }) => {
  let m1Value = ''
  let m1Label = ''
  let m2Value = ''
  let m2Label = ''
  let mobileSubText = ''
  let desktopSubText = ''
  let challengeType = challengeStatus?.isTeamChallenge ? 'teamResults' : 'userResults'

  if (!user?.id) return { 
    m1Value: null, 
    m1Label: null, 
    m2Value: null, 
    m2Label: null, 
    mobileSubText: null, 
    desktopSubText: null 
  }
  
  if (!challengeStatus) return { m1Value, m1Label, m2Value, m2Label, mobileSubText, desktopSubText }

  switch (challengeStatus.measure) {
    case 'distance':
      if (challengeStatus.isRank) {
        m1Value = formatMetric(challengeStatus[challengeType]?.distance, 2, 'lg', user.imperial)
        m1Label = displayMeasurement(user.imperial)
        m2Value = formatNumber(challengeStatus.dayOfChallenge)
        m2Label = 'Days In'
        mobileSubText = `${m1Value} ${m1Label.toLowerCase()} in ${m2Value} days`
      } else {
        m1Value = formatMetric(challengeStatus[challengeType]?.distance, 2, 'lg', user.imperial)
        m1Label = displayMeasurement(user.imperial)
        m2Value = formatMetric(challengeStatus[challengeType]?.remaining, 2, 'lg', user.imperial)
        m2Label = `Remaining`
        mobileSubText = `${m1Value} ${m1Label.toLowerCase()} • ${m2Value} ${m2Label.toLowerCase()}`
      }
      break;
    case 'elevation':
      if (challengeStatus.isRank) {
        m1Value = formatMetric(challengeStatus[challengeType]?.elevation, 0, 'sm', user.imperial)
        m1Label = displayMeasurement(user.imperial, 'sm')
        m2Value = formatNumber(challengeStatus.dayOfChallenge)
        m2Label = 'Days In'
        mobileSubText = `${m1Value} ${m1Label.toLowerCase()} in ${m2Value} days`
      } else {
        m1Value = formatMetric(challengeStatus[challengeType]?.elevation, 0, 'sm', user.imperial)
        m1Label = displayMeasurement(user.imperial, 'sm')
        m2Value = formatMetric(challengeStatus[challengeType]?.remaining, 0, 'sm', user.imperial)
        m2Label = 'Remaining'
        mobileSubText = `${m1Value} ${m1Label.toLowerCase()} • ${m2Value} remaining`
      }
      break;
    case 'durationGoal':
      if (challengeStatus.isRank) {  
        m1Value = formatDuration(challengeStatus[challengeType]?.durationGoal)
        m1Label = 'Duration'
        m2Value = formatNumber(challengeStatus.dayOfChallenge)
        m2Label = 'Days In'
        mobileSubText = `${m1Value} in ${m2Value} days`
      } else {
        m1Value = formatDuration(challengeStatus[challengeType]?.durationGoal)
        m1Label = 'Duration'
        m2Value = formatDuration(challengeStatus[challengeType]?.remaining)
        m2Label = 'Remaining'
        mobileSubText = `${m1Value} • ${m2Value} remaining`
      }
      break;
    case 'pointGoal':
      if (challengeStatus.isRank) {  
        m1Value = formatNumber(challengeStatus[challengeType]?.pointGoal, 2)
        m1Label = 'Points'
        m2Value = formatNumber(challengeStatus.dayOfChallenge)
        m2Label = 'Days In'
        mobileSubText = `${m1Value} ${m1Label.toLowerCase()} in ${m2Value} days`
      } else {
        m1Value = formatNumber(challengeStatus[challengeType]?.pointGoal, 2)
        m1Label = 'Points'
        m2Value = formatNumber(challengeStatus[challengeType]?.remaining, 2)
        m2Label = 'Remaining'
        mobileSubText = `${m1Value} points • ${m2Value.toLowerCase()} ${m2Label.toLowerCase()}`
      }
      break;
    case 'activityGoal':
      if (challengeStatus.isRank) {  
        m1Value = formatNumber(challengeStatus[challengeType]?.activityGoal)
        m1Label = 'Activities'
        m2Value = formatNumber(challengeStatus.dayOfChallenge)
        m2Label = 'Days In'
        mobileSubText = `${m1Value} ${m1Label.toLowerCase()} in ${m2Value} days`
      } else {
        m1Value = formatNumber(challengeStatus[challengeType]?.activityGoal)
        m1Label = 'Activities'
        m2Value = formatNumber(challengeStatus[challengeType]?.remaining)
        m2Label = 'Remaining'
        mobileSubText = `${m1Value} ${m1Label.toLowerCase()} • ${m2Value} ${m2Label.toLowerCase()}`
      }
      break;
    case 'streakGoal':
      const longest = challengeStatus[challengeType]?.longestStreak
      let current = challengeStatus[challengeType]?.currentStreak
      let status = 'Pending'
      if (longest >= challengeStatus.streakGoal) {
        status = 'Complete'
        current = longest
      } else if (current && challengeStatus[challengeType]?.remaining <= challengeStatus.daysLeft) {
        status = 'Active'
      } else if (challengeStatus[challengeType]?.remaining > challengeStatus.daysLeft) {
        status = 'Broken'
      }
      m1Value = formatNumber(current)
      m1Label = 'Current Streak'
      m2Value = formatNumber(challengeStatus[challengeType]?.remaining)
      m2Label = 'Days Left'
      mobileSubText = `Streak ${status} • ${m1Value} Days • ${m2Value} Left`
      break;
  }

  desktopSubText = challengeStatus.start ? `${formatDate(challengeStatus.start)} - ${formatDate(challengeStatus.end)}` : ''
  if (challengeStatus.scheduled) {
    mobileSubText = challengeStatus.start ? `Starts in ${differenceInDays(new Date(challengeStatus.start), startOfDay(new Date()))} Days` : ''
    desktopSubText = mobileSubText
  }
  if ((challengeStatus.teamResults && challengeStatus.teamResults.completed) || (challengeStatus.userResults && challengeStatus.userResults.completed)) {
    mobileSubText = <><FaTrophy className='text-secondary float-left mr-1' /> {mobileSubText}</>
    desktopSubText = mobileSubText
  } else if (challengeStatus.over) {
    mobileSubText = `Ended • ${mobileSubText}`
    desktopSubText = mobileSubText
  }
      
  return { m1Value, m1Label, m2Value, m2Label, mobileSubText, desktopSubText }
}

const ChallengeNav = ({ challenge }) => {
  const { user } = useUser()
  
  const challengeStatusHook = useChallengeStatus(user?.token, challenge?.id)
  const challengeStatus = challengeStatusHook.challenge
  if (!challenge) return null

  const { m1Value, m1Label, m2Value, m2Label, mobileSubText, desktopSubText } = getNavbarStats({ user, challengeStatus })

  return <>
    <NavbarItem className='min-w-[48px] min-h-[48px] rounded-full overflow-hidden relative md:ml-4'>
      <HeaderImage name={challenge.challengeName} image={challenge.logo} />
    </NavbarItem>
  
    <NavbarItem className='grow mr-4 md:mr-0 h-[50px] flex justify-start items-center'>
      <div className='flex flex-col gap-0'>
        <div className='w-full text-md md:text-xl truncate py-0 leading-6 mb-0'>{challenge?.challengeName}</div>
        {challengeStatus && <div className='w-full text-sm text-chTan-600 leading-none'>
          <span className='md:hidden'>{mobileSubText}</span>
          <span className='hidden md:block'>{desktopSubText}</span>
        </div>}
      </div>
    </NavbarItem>
    
    <NavbarItem className='hidden md:block'>
      {(challengeStatus && challengeStatus?.id) && 
        <div className='bg-chTan-200 w-[300px] text-center leading-tight grid grid-cols-2 py-2 mr-2 gap-1'>
          <div className='border-r-[1px]' style={{borderRightWidth: '1px', borderRightColor: 'var(--color-primary)'}}>
            <div className='text-lg text-primary font-bold'>{m1Value}</div>
            <div className='text-sm text-chTan-600'>{m1Label}</div>
          </div>
          <div>
            <div className='text-lg text-primary font-bold'>{m2Value}</div>
            <div className='text-sm text-chTan-600'>{m2Label}</div>
          </div>
        </div>
      }
    </NavbarItem>
  </>
}

const GroupNav = ({ group }) => {
  const { user } = useUser()
  
  if (!group) return null

  return <>
    <NavbarItem className='min-w-[48px] min-h-[48px] rounded-full overflow-hidden relative md:ml-4'>
      <HeaderImage name={group.name} image={group.logo} />
    </NavbarItem>
  
    <NavbarItem className='grow mr-4 md:mr-0 h-[50px] flex justify-start items-center'>
      <div className='flex flex-col gap-0'>
        <div className='w-full text-md md:text-xl truncate py-0 leading-none mb-1'>{group?.name}</div>
      </div>
    </NavbarItem>

    {/* <NavbarItem className='hidden md:block h-[56px]'>&nbsp;</NavbarItem> */}
  </>
}

const AccountSubNav = ({ section, page }) => {
  const router = useRouter()
  
  const { user } = useUser()
  const [isAdminPopoverOpen, setIsAdminPopoverOpen] = useState(false)

  if (section !== 'account' && section !== 'account-admin') return null

  return (
    <div className='w-full md:ml-[300px] md:max-w-[calc(100vw-300px)] bg-chTan-100 -mt-5 pb-2 px-4'>
      <div className='flex gap-2 w-full md:max-w-[450px] px-0'>
        <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'account' && page === 'account - profile' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`} 
            onClick={() => {
              if (page !== 'profile') {
                router.push(`/account/profile`) 
              }
            }}
            >Profile</Chip>
        </div>
        {user.organization && <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'account' && page === 'account - organization' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              if (page !== 'organization') {
                router.push(`/account/organization`) 
              }
            }}
            >Organization</Chip>
        </div>}
        {user.organization && <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'account' && page === 'account - credits' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              if (page !== 'credits') {
                router.push(`/account/credits`) 
              }
            }}
            >Credits</Chip>
        </div>}
        {user.organization && <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'account' && page === 'account - payments' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              if (page !== 'payments') {
                router.push(`/account/payments`) 
              }
            }}
            >Payments</Chip>
        </div>}
        {!user.isAdmin && <div className='w-full'>
          <Chip 
            size='sm' 
            color='primary' 
            className={`w-full !min-w-full text-center ${section === 'account' && page === 'signout' ? '' : 'cursor-pointer bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
            onClick={() => {
              router.push(`/sign-out`)
            }}
            >Sign Out</Chip>
        </div>}
        {user.isAdmin && <div className='w-full'>
          <Popover placement='bottom-start' size='lg' isOpen={isAdminPopoverOpen} onOpenChange={(open) => setIsAdminPopoverOpen(open)}>
            <PopoverTrigger className="w-full">
              <Chip 
                size='sm' 
                color='primary' 
                className={`w-full !min-w-full text-center cursor-pointer ${section === 'account-admin' ? '' : 'bg-chTan-200 text-chTan-600 hover:bg-chTan-300'}`}
                >Admin</Chip>
            </PopoverTrigger>
            <PopoverContent>
              <Listbox 
                aria-label="Main Menu" 
                itemClasses={{ 
                  base: 'rounded-none text-chDarkBlue data-[hover=true]:bg-chTan-300', 
                  title: 'text-md' 
                }}>
                  <ListboxItem 
                    onPress={() => {
                      setIsAdminPopoverOpen(false)  
                    }}
                    href={`/account/houndalytics`}
                    title='Houndalytics' 
                    as={Link}
                    prefetch={false}
                    />
                  <ListboxItem 
                    onPress={() => {
                      setIsAdminPopoverOpen(false)  
                    }}
                    href={`/account/houndmin`}
                    title='Houndmin' 
                    as={Link}
                    prefetch={false}
                    />
                  <ListboxItem 
                    onPress={() => {
                      setIsAdminPopoverOpen(false)
                    }}
                    href={`/sign-out`}
                    title='Sign Out' 
                    as={Link}
                    prefetch={false}
                    />
              </Listbox>
            </PopoverContent>
          </Popover>
        </div>}
      </div>
    </div>
  )
}

const Header = ({ section, page }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [canGoBack, setCanGoBack] = useState(false)
  const path = usePathname()
  const { challenge } = useChallenge()
  const { group } = useGroup()
  const router = useRouter()

  useEffect(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) setCanGoBack(true)
  }, [])
  
  const handleBackClick = () => {
    router.back()
  }

  let title = ''
  if (path.endsWith('/profile')) {
    page = 'account - profile'
  } else if (path.endsWith('/organization')) {
    page = 'account - organization'
  } else if (path.endsWith('/credits')) {
    page = 'account - credits'
  } else if (path.endsWith('/payments')) {
    page = 'account - payments'
  } else if (path.endsWith('/houndalytics')) {
    section = 'account-admin'
    page = 'houndalytics'
  } else if (path.endsWith('/houndmin')) {
    section = 'account-admin'
    page = 'Houndmin'
  } else if (path.endsWith('/results')) {
    page = 'results'
    title = 'Group Results'
  } else if (section === 'groups' && path.endsWith('/activities')) {
    page = 'activities'
    title = 'Group Activities'
  } else if (section === 'groups' && path.endsWith('/edit')) {
    section = 'group-admin'
    page = 'edit'
    title = 'Edit Group'
  } else if (group && section === 'groups') {
    page = 'challenges'
    title = group.type === 'challenges' ? 'Group Challenges' : 'Group Teams'
  } else if (section === 'challenges' && path.endsWith('/edit')) {
    page = 'challenge-edit'
    title = 'Create Challenge'
  }

  // Determine the navbar height based on conditions
  const navbarHeight = (challenge || group) ? '6rem' : '4rem' // Increased for two rows
  
  return <Navbar 
    isMenuOpen={isMenuOpen} 
    onMenuOpenChange={setIsMenuOpen} 
    maxWidth='full'
    isBlurred={false}
    classNames={{ base: 'fixed items-start', wrapper: 'px-0 md:px-0 flex-col' }} // Changed to flex-col
    style={{ '--navbar-height': navbarHeight }} // Set the CSS variable
    >
    
    {/* First row */}
    <NavbarContent justify="start" className='w-full md:ml-[300px] md:max-w-[calc(100vw-300px)] gap-3 bg-chTan-100 border-b border-chTan-200'>
      <NavbarMenuToggle className='ml-2 md:ml-4 md:hidden' aria-label={isMenuOpen ? "Close menu" : "Open menu"} />
      
      {/* Back button - only shown when there's history to go back to */}
      {(canGoBack && (section === 'apps' || (section === 'activity' && page === 'Activity Details'))) ? <>
        <NavbarItem >
          <Button
            isIconOnly
            variant='light'
            onPress={handleBackClick}
            className="mx-2"
            aria-label="Go back"
          >
            <FaArrowLeft size={18} />
          </Button>
        </NavbarItem>
        <NavbarItem className='w-[calc(100vw-180px)] mr-4 md:mr-0 h-[50px] mt-2 '>
          <div className='text-2xl capitalize pr-6 pt-2 truncate'>{title || page}</div>
        </NavbarItem>
        </> : <>
        {(!group && !challenge) && <NavbarItem className='grow mr-4 md:mr-0 h-[50px] mt-2 '>
          <div className='h-full pl-4 text-2xl capitalize pr-6 pt-2 truncate'>
            {section === 'challenges' && page === 'challenges' ? 'Get Started' : (title || page)}
          </div>
        </NavbarItem>}  
      </>}
      
      <ChallengeNav challenge={challenge} />
      <GroupNav group={group} />
      
    </NavbarContent>
    
    <GroupSubNav section={section} page={page} title={title} group={group} />
    <ChallengeSubNav section={section} page={page} challenge={challenge} />
    <AccountSubNav section={section} page={page} />
    
    {/* Second row */}  
    
    <div className='fixed top-0 left-0 w-[300px] h-screen bg-chTan-300 hidden md:block'>
      <Menu challengeId={challenge?.id} section={section} page={page} />
    </div>
    
    <NavbarMenu 
      motionProps={{
        variants: {
          enter: {
            x: 0,
            opacity: 1,
            transition: {
              duration: 0.3,
              ease: "easeOut",
            },
          },
          exit: {
            x: "-100%",
            opacity: 80,
            transition: {
              duration: 0.2,
              ease: "easeIn",
            },
          }
        },
        initial: "exit",
        animate: "enter",
        exit: "exit",
      }}
      className='bg-chTan-300 block md:hidden p-0' style={{ marginTop: '-64px', zIndex: 40 }} >
      <Menu setIsMenuOpen={setIsMenuOpen} challengeId={challenge?.id} section={section} page={page} />
    </NavbarMenu>
  </Navbar>
}

export default Header
